import React, { useEffect, useRef, useState } from 'react';

/* ============================================================
   自定义光标（HUD 准星）· 仅作用于 AI 指挥办公室
   - 系统光标隐藏，改用跟随鼠标的自绘层，支持动态形态
   - 状态：idle 待机准星 / hover 可交互(环收紧+提亮) /
     lock 悬停工作台(四角锁定框+十字准星) / down 按下收缩 /
     text 文本输入区(隐藏自绘、露出系统文本光标)
   矢量绘制而非贴图：任意分辨率锐利，且可旋转/脉动/变形。
   ============================================================ */

type St = 'idle' | 'hover' | 'lock' | 'down' | 'text';

const LOCK_SEL = '.aco12-spot[role="button"]';
const TEXT_SEL = 'input:not([type="button"]):not([type="submit"]), textarea, [contenteditable="true"]';
const HOVER_SEL = 'a, button, [role="button"], select, .imgcard, .aco17-pod, .aco3-agent-chip, .aco3-fan-card, .st-agent, .mi, .di, .tab, .prow, .aco3-legend button, .aco20-mention button, .fi, .si, .ri';

const CursorFX: React.FC = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef<St>('idle');
  const downRef = useRef(false);
  const [state, setState] = useState<St>('idle');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const apply = (s: St) => {
      if (s !== stateRef.current) { stateRef.current = s; setState(s); }
    };
    const resolve = (target: EventTarget | null): St => {
      const t = target as HTMLElement | null;
      if (!t || !t.closest) return 'idle';
      if (t.closest(TEXT_SEL)) return 'text';
      if (downRef.current) return 'down';
      if (t.closest(LOCK_SEL)) return 'lock';
      if (t.closest(HOVER_SEL)) return 'hover';
      return 'idle';
    };
    const move = (e: MouseEvent) => {
      const el = ref.current;
      if (!el) return;
      if (!visible) setVisible(true);
      el.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      apply(resolve(e.target));
    };
    const down = (e: MouseEvent) => {
      downRef.current = true;
      if (stateRef.current !== 'text') apply('down');
      void e;
    };
    const up = (e: MouseEvent) => {
      downRef.current = false;
      apply(resolve(e.target));
    };
    const leave = () => setVisible(false);
    const enter = () => setVisible(true);
    window.addEventListener('mousemove', move, { passive: true });
    window.addEventListener('mousedown', down, { passive: true });
    window.addEventListener('mouseup', up, { passive: true });
    document.addEventListener('mouseleave', leave);
    document.addEventListener('mouseenter', enter);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      document.removeEventListener('mouseleave', leave);
      document.removeEventListener('mouseenter', enter);
    };
  }, [visible]);

  return (
    <div ref={ref} className={`aco33-cursor s-${state} ${visible ? 'on' : ''}`} aria-hidden>
      <span className="glow" />
      <span className="ring outer" />
      <span className="ring inner" />
      <span className="core" />
      <span className="cross v" />
      <span className="cross h" />
      <i className="br tl" /><i className="br tr" /><i className="br bl" /><i className="br br2" />
    </div>
  );
};

export default CursorFX;
